import { NoteEditorTopBarShareButton } from "@/components/note/editor/top-bar/note-editor-top-bar-share-button";
import { OrganizeNote } from "@/components/note/editor/top-bar/organize";
import { ItemListRowFeaturedContent } from "@/components/item-list/rows/components/item-list-row-featured-content";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef } from "react";
import {
  MDSPanelHeaderButtonGroup,
  MDSEditableTitle,
  MDSTextVariant,
  MDSPanelHeader,
  MDSPanelHeaderSlotName,
  MDSIconButton,
  MDSIconKind,
  MDSMenuDropdownSlotName,
  MDSMenuContentList,
  MDSMenuContentItemKind,
} from "@/mds";
import { TemplatesEditorBanner } from "@/pages/templates-view/components/templates-editor-banner";
import { MDSActionMenuDropdown } from "@/mds/components/menu/action-menu/action-menu-dropdown";
import { actions } from "@/actions";

interface TemplateEditorTopBarProps {
  template?: TemplateObservable;
}

export const TemplateEditorTopBar = observer<TemplateEditorTopBarProps>(
  function TemplateEditorTopBar({ template }) {
    const page = useTabPageStore("templatesViewPage");

    const templateTitleInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
      if (template && !template.title) templateTitleInputRef.current?.focus();
    }, [template]);

    useEffect(() => {
      // Make sure to save the template when the component unmounts because blur is not guaranteed
      // to be called when navigating away from the page.
      return () => {
        if (template && !template.isDeleted) template.save();
      };
    }, [template]);

    const menuContent: MDSMenuContentList = useMemo(
      () => [
        {
          id: "copy-template-link",
          kind: MDSMenuContentItemKind.Item,
          label: "Copy link",
          icon: MDSIconKind.Link,
          onClick: () => {
            if (!template) return;
            actions.copyTemplateLinkToClipboard({ templateId: template.id });
          },
        },
        {
          id: "delete-template",
          kind: MDSMenuContentItemKind.Item,
          label: "Delete",
          icon: MDSIconKind.Trash,
          onClick: () => {
            if (!template) return;
            page.deleteTemplateModal.open({ templateId: template.id });
          },
        },
      ],
      [template, page.deleteTemplateModal]
    );

    if (!template) return null;
    const isDefaultCalendarEventTemplate = template.isDefaultCalendarEventTemplate;

    return (
      <Wrapper>
        <MDSPanelHeader>
          <StyledHeader slot={MDSPanelHeaderSlotName.Heading}>
            <MDSEditableTitle
              variant={MDSTextVariant.HeaderM}
              value={template.title}
              placeholder={"Template name"}
              onFocus={() => template.lock()}
              onChange={e => template.setTitle(e.currentTarget.value)}
              onBlur={async () => await template.save()}
              autoFocus={template?.title === ""}
              readOnly={!template.canWrite || template.isDefaultCalendarEventTemplate}
            />
            <OrganizeNoteContainer>
              <OrganizeNote note={template} />
              {!!template.collectionList.allCollections.length && (
                <ItemListRowFeaturedContent
                  collections={sortCollectionsForChips(template.collectionList.allCollections)}
                />
              )}
            </OrganizeNoteContainer>
          </StyledHeader>
          {!isDefaultCalendarEventTemplate && (
            <StyledButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
              <NoteEditorTopBarShareButton note={template} shareSheetModal={page.shareSheetModal} />
              <MDSActionMenuDropdown contentList={menuContent} placement="bottom right">
                <MDSIconButton slot={MDSMenuDropdownSlotName.Trigger} iconKind={MDSIconKind.More} />
              </MDSActionMenuDropdown>
            </StyledButtonGroup>
          )}
        </MDSPanelHeader>
        <TemplatesEditorBanner template={template} />
      </Wrapper>
    );
  }
);

const Wrapper = styled.div({});

const StyledHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const OrganizeNoteContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledButtonGroup = styled(MDSPanelHeaderButtonGroup)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
});
